// Local Storage Mode - an opt-in, account-level choice. When enabled, people
// data is written to the browser instead of Supabase. Everything else about
// Knowroom (AI calls, chat, research) keeps working the same way - only where
// the data itself lives changes.
//
// Records are shaped exactly like a raw Supabase `donors` row (snake_case
// columns: last_contact, next_meeting, key_details, etc.) rather than any
// app-specific format. That's deliberate: it means existing converters like
// supabaseDonorToAppFormat() work on local records completely unchanged, and
// migrating to Supabase later is still a single command - toImportConfirmContacts()
// flattens these into what POST /api/import-confirm already expects.

const MODE_KEY = 'knowroom-local-mode-enabled';
const DONORS_KEY = 'knowroom-local-donors';

export function isLocalModeEnabled() {
  return localStorage.getItem(MODE_KEY) === 'true';
}

export function setLocalModeEnabled(enabled) {
  localStorage.setItem(MODE_KEY, enabled ? 'true' : 'false');
}

export function getLocalDonors() {
  try {
    const raw = localStorage.getItem(DONORS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error('localMode: failed to read local donors', e);
    return [];
  }
}

export function saveLocalDonors(donors) {
  localStorage.setItem(DONORS_KEY, JSON.stringify(donors));
}

function newLocalId() {
  return (crypto.randomUUID ? crypto.randomUUID() : `local-${Date.now()}-${Math.random().toString(36).slice(2)}`);
}

// Builds a raw-row-shaped record with sensible defaults - matches what a real
// Supabase `donors` row looks like, column for column.
export function createLocalDonorRecord(fields = {}) {
  const now = new Date().toISOString();
  return {
    id: fields.id || newLocalId(),
    name: fields.name,
    amount: fields.amount || 'Unknown',
    narrative: fields.narrative || '',
    last_contact: fields.last_contact || 'Unknown',
    stage: fields.stage || 'prospecting',
    next_meeting: fields.next_meeting || null,
    next_task: fields.next_task || null,
    created_at: fields.created_at || now,
    updated_at: now,
    group_id: fields.group_id || null,
    group_ids: fields.group_ids || [],
    insight_messages: fields.insight_messages || [],
    timeline_events: fields.timeline_events || [],
    key_details: fields.key_details || [],
    tasks: fields.tasks || [],
    email: fields.email || '',
    phone: fields.phone || '',
    address: fields.address || '',
    organization: fields.organization || '',
    title: fields.title || ''
  };
}

export function addLocalDonor(fields) {
  const donors = getLocalDonors();
  const record = createLocalDonorRecord(fields);
  donors.push(record);
  saveLocalDonors(donors);
  return record;
}

export function getLocalDonorByName(name) {
  return getLocalDonors().find(d => d.name === name) || null;
}

export function updateLocalDonor(name, updates) {
  const donors = getLocalDonors();
  const index = donors.findIndex(d => d.name === name);
  if (index === -1) return null;
  donors[index] = { ...donors[index], ...updates, updated_at: new Date().toISOString() };
  saveLocalDonors(donors);
  return donors[index];
}

export function deleteLocalDonor(name) {
  const donors = getLocalDonors().filter(d => d.name !== name);
  saveLocalDonors(donors);
}

export function clearLocalDonors() {
  localStorage.removeItem(DONORS_KEY);
}

// Flattens local records into what POST /api/import-confirm expects, for
// migrating a Local Mode account up to Supabase in one call.
export function toImportConfirmContacts() {
  return getLocalDonors().map(d => ({
    name: d.name,
    amount: d.amount,
    email: d.email,
    phone: d.phone,
    lastContact: d.last_contact,
    notes: d.narrative,
    stage: d.stage
  }));
}

// --- Groups ---
// Same raw-row-shape approach as donors, so supabaseGroupToAppFormat() works
// on local groups unchanged.

const GROUPS_KEY = 'knowroom-local-groups';

export function getLocalGroups() {
  try {
    const raw = localStorage.getItem(GROUPS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error('localMode: failed to read local groups', e);
    return [];
  }
}

export function saveLocalGroups(groups) {
  localStorage.setItem(GROUPS_KEY, JSON.stringify(groups));
}

export function addLocalGroup(name, color = '#8b5cf6', context = null) {
  const groups = getLocalGroups();
  const record = {
    id: newLocalId(),
    name,
    color,
    context: context || null,
    created_at: new Date().toISOString()
  };
  groups.push(record);
  saveLocalGroups(groups);
  return record;
}

export function deleteLocalGroup(groupId) {
  saveLocalGroups(getLocalGroups().filter(g => g.id !== groupId));
}

// Adds an existing local donor to a local group - group_id mirrors Supabase's
// single-group field, group_ids covers the multi-group case the rest of the
// app also reads.
export function addLocalDonorToGroup(donorName, groupId) {
  const donor = getLocalDonorByName(donorName);
  if (!donor) return null;
  const group_ids = Array.from(new Set([...(donor.group_ids || []), groupId]));
  return updateLocalDonor(donorName, { group_id: groupId, group_ids });
}

// --- Lists & Tables ---
// Unlike donors/groups, these are stored in the same app-ready shape the
// server already returns from GET /api/lists and /api/tables (camelCase +
// group_id) rather than a raw-row shape, since nothing else needs to convert
// them - the create endpoints already return exactly this shape too.

const LISTS_KEY = 'knowroom-local-lists';
const TABLES_KEY = 'knowroom-local-tables';

export function getLocalLists() {
  try {
    const raw = localStorage.getItem(LISTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error('localMode: failed to read local lists', e);
    return [];
  }
}

export function saveLocalLists(lists) {
  localStorage.setItem(LISTS_KEY, JSON.stringify(lists));
}

export function addLocalList(list) {
  const lists = getLocalLists();
  const record = { ...list, id: list.id || newLocalId(), createdAt: list.createdAt || new Date().toISOString() };
  lists.unshift(record);
  saveLocalLists(lists);
  return record;
}

export function deleteLocalList(listId) {
  saveLocalLists(getLocalLists().filter(l => l.id !== listId));
}

export function getLocalTables() {
  try {
    const raw = localStorage.getItem(TABLES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error('localMode: failed to read local tables', e);
    return [];
  }
}

export function saveLocalTables(tables) {
  localStorage.setItem(TABLES_KEY, JSON.stringify(tables));
}

export function addLocalTable(table) {
  const tables = getLocalTables();
  const record = { ...table, id: table.id || newLocalId(), createdAt: table.createdAt || new Date().toISOString() };
  tables.unshift(record);
  saveLocalTables(tables);
  return record;
}

export function deleteLocalTable(tableId) {
  saveLocalTables(getLocalTables().filter(t => t.id !== tableId));
}

// --- Feed / chat history ---
// Chat messages sent while in Local Mode must never touch the real Supabase
// feed_entries table - that would leak conversation history (and whatever
// real donor details it contains) between the two, in both directions.

const FEED_KEY = 'knowroom-local-feed';

export function getLocalFeedEntries() {
  try {
    const raw = localStorage.getItem(FEED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error('localMode: failed to read local feed', e);
    return [];
  }
}

export function saveLocalFeedEntries(entries) {
  localStorage.setItem(FEED_KEY, JSON.stringify(entries.slice(0, 100)));
}

export function addLocalFeedEntry(entry) {
  const entries = getLocalFeedEntries();
  const existingIndex = entries.findIndex(e => e.id === entry.id);
  if (existingIndex !== -1) {
    entries[existingIndex] = entry;
  } else {
    entries.unshift(entry);
  }
  saveLocalFeedEntries(entries);
}
